import { useEffect, useState } from 'react';
import { QuestionWrapper, useFormField, type Group } from '@stepform';

export interface NotifiableEventsFieldSchema {
  type: 'NotifiableEvents';
  id: string;
  label?: string;
  optional?: boolean;
  helpText?: string;
  supportText?: string;
  min?: number;
  max?: number;
  minTotal?: number;
}

function parseAnswer(answer: unknown): [number, number, number] {
  if (Array.isArray(answer) && answer.length >= 3) {
    return [Number(answer[0] ?? 0), Number(answer[1] ?? 0), Number(answer[2] ?? 0)];
  }

  return [0, 0, 0];
}

export function NotifiableEventsField({
  fieldSchema,
  group,
}: {
  fieldSchema: NotifiableEventsFieldSchema;
  group: Group;
}) {
  const field = useFormField(fieldSchema as never, group);
  const [employees, setEmployees] = useState<number>(() => parseAnswer(field.answer)[0]);
  const [others, setOthers] = useState<number>(() => parseAnswer(field.answer)[1]);
  const [both, setBoth] = useState<number>(() => parseAnswer(field.answer)[2]);

  useEffect(() => {
    field.setAnswer([employees, others, both]);
  }, [employees, others, both, field]);

  if (!field.show) {
    return null;
  }

  return (
    <QuestionWrapper {...field} fieldSchema={fieldSchema as never}>
      <p className="type--small mt-2">
        <strong>Categorise these by number of:</strong>
      </p>
      <div className="answer-group answer-group--stacked">
        <div className="answer">
          <label className="small-label label--light">
            <span>
              notifiable events involving <strong>only</strong> your employees
            </span>
            <input
              className="text sm"
              type="number"
              min={fieldSchema.min}
              max={fieldSchema.max}
              name="Employees"
              id="Employees"
              value={employees}
              onChange={(e) => setEmployees(Number(e.target.value))}
            />
          </label>
        </div>
        <div className="answer">
          <label className="small-label label--light">
            <span>
              notifiable events involving <strong>only</strong> other workers
            </span>
            <input
              className="text sm"
              type="number"
              min={fieldSchema.min}
              max={fieldSchema.max}
              name="OtherWorkers"
              id="OtherWorkers"
              value={others}
              onChange={(e) => setOthers(Number(e.target.value))}
            />
          </label>
        </div>
        <div className="answer">
          <label className="small-label label--light">
            <span>
              notifiable events involving <strong>both</strong> your employees and other workers
            </span>
            <input
              className="text sm"
              type="number"
              min={fieldSchema.min}
              max={fieldSchema.max}
              name="Both"
              id="Both"
              value={both}
              onChange={(e) => setBoth(Number(e.target.value))}
            />
          </label>
        </div>
      </div>
    </QuestionWrapper>
  );
}

export function formatNotifiableEventsAnswer(answer: unknown): string {
  if (!Array.isArray(answer) || answer.length < 3) {
    return '';
  }

  const employees = Number(answer[0]);
  const others = Number(answer[1]);
  const both = Number(answer[2]);

  if (Number.isNaN(employees) || Number.isNaN(others) || Number.isNaN(both)) {
    return '';
  }

  return `<strong>Employees:</strong> ${employees.toLocaleString()}<br><strong>Other workers:</strong> ${others.toLocaleString()}<br><strong>Both employees and other workers:</strong> ${both.toLocaleString()}`;
}
